import {
  getPatientsFromEvokeIds,
  findPatientRecordsByNameOrDobOrEvokeId,
} from "app/services/patient/patient";
import { useCallback, useEffect, useState } from "react";
import { showErrorToast } from "utils/toast";
import useDebounce from "app/hooks/useDebounce";

const usePatientListSelection = (patientList, handleConfirm, handleClose) => {
  const [evokeIds, setEvokeIds] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [patientRecords, setPatientRecords] = useState([]);
  const [originalPatientRecords, setOriginalPatientRecords] = useState([]);
  const [selectedPatientRecordEvokeId, setSelectedPatientRecordEvokeId] =
    useState(null);
  const [isPatientListLoaded, setIsPatientListLoaded] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);

  const debouncedSearch = useCallback(
    useDebounce(async (searchValue) => {
      if (!searchValue || searchValue.length < 3) {
        setSearchResults([]);
        setHasSearched(false);
        return;
      }
      setIsLoading(true);
      try {
        const result =
          await findPatientRecordsByNameOrDobOrEvokeId(searchValue);
        setSearchResults(result?.patients || []);
      } catch (error) {
        showErrorToast(error?.message);
        setSearchResults([]);
      } finally {
        setIsLoading(false);
        setHasSearched(true);
      }
    }, 750),
    []
  );

  useEffect(() => {
    if (patientList.length) {
      setPatientRecords(patientList);
      setOriginalPatientRecords(patientList);
      setIsPatientListLoaded(true);
    }
  }, [patientList]);

  useEffect(() => {
    if (inputValue) {
      setHasSearched(false);
      setSearchResults([]);
      debouncedSearch(inputValue);
    } else {
      setSearchResults([]);
      setHasSearched(false);
    }
  }, [inputValue, debouncedSearch]);

  const handlePatientFilter = (patientName) => {
    if (!patientName) {
      setPatientRecords(originalPatientRecords);
    }

    setPatientRecords((prevRecords) =>
      prevRecords.filter(
        (record) =>
          record.firstName.toLowerCase().includes(patientName.toLowerCase()) ||
          record.lastName.toLowerCase().includes(patientName.toLowerCase())
      )
    );
  };

  const handlePatientEvokeIdEntry = (evokeIds) => {
    const newEvokeIds = evokeIds
      ?.map((value) => value?.trim())
      ?.filter(Boolean);

    handlePatientSearch(newEvokeIds);
  };

  const handlePatientSearch = async (selectedEvokeIds) => {
    try {
      setEvokeIds(selectedEvokeIds);
      if (!selectedEvokeIds.length) {
        return;
      }

      setIsLoading(true);
      const result = await getPatientsFromEvokeIds(selectedEvokeIds);
      const patientsFound = result?.data?.patients;
      const evokeIdsNotFound = result?.data?.nonExistentEvokeIds;
      if (patientsFound.length) {
        setPatientRecords((prev) => {
          const existingIds = prev.map((p) => p.evoke_id);
          const newPatients = patientsFound.filter(
            (p) => !existingIds.includes(p.evoke_id)
          );
          return [...prev, ...newPatients];
        });
        setIsPatientListLoaded(true);
        setInputValue("");
      }

      if (evokeIdsNotFound?.length > 0) {
        showErrorToast(
          `The following evoke ids are not found: ${evokeIdsNotFound.join()}`
        );
      }
    } catch (error) {
      showErrorToast(error?.message);
    } finally {
      setIsLoading(false);
      setSelectedPatientRecordEvokeId(null);
    }
  };

  const handlePatientRecordSelect = (patientRecord) => {
    setSelectedPatientRecordEvokeId(patientRecord);
  };

  const handlePatientConfirm = async () => {
    const patientRecordList = [...patientRecords];
    const selectedPatientIndex = patientRecordList.findIndex(
      (record) => record?.evoke_id === selectedPatientRecordEvokeId
    );
    const selectedPatientRecord = patientRecordList.splice(
      selectedPatientIndex,
      1
    )[0];
    await handleConfirm({
      selectedPatientRecord,
      remainingPatientRecords: patientRecordList,
    });
    setEvokeIds(
      selectedPatientRecordEvokeId ? [selectedPatientRecordEvokeId] : []
    );
    setPatientRecords(selectedPatientRecord ? [selectedPatientRecord] : []);
    setSelectedPatientRecordEvokeId(null);
    setShowConfirmation(false);
    handleClose && handleClose();
  };

  const handleClearPatientRecords = () => {
    setPatientRecords([]);
    setSelectedPatientRecordEvokeId(null);
    setEvokeIds([]);
  };

  const handleRemovePatient = (evokeId) => {
    setPatientRecords((prev) => prev.filter((p) => p.evoke_id !== evokeId));
    if (selectedPatientRecordEvokeId === evokeId) {
      setSelectedPatientRecordEvokeId(null);
    }
    setEvokeIds((prev) => prev.filter((id) => id !== evokeId));
  };

  return {
    evokeIds,
    isLoading,
    inputValue,
    patientRecords,
    showConfirmation,
    isPatientListLoaded,
    selectedPatientRecordEvokeId,
    searchResults,
    hasSearched,

    setEvokeIds,
    setIsLoading,
    setInputValue,
    setPatientRecords,
    handlePatientFilter,
    setShowConfirmation,
    handlePatientConfirm,
    handlePatientEvokeIdEntry,
    handlePatientRecordSelect,
    setSelectedPatientRecordEvokeId,
    handleClearPatientRecords,
    handleRemovePatient,
  };
};

export default usePatientListSelection;
